import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Calendar } from "@/shared/components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import {
  Search,
  Calendar as CalendarIcon,
  ArrowUpFromLine,
  Copy,
  Check,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  X,
  Download,
  Building,
  Plus,
  History,
} from "lucide-react";
import EngineLayout from "@/shared/components/engine/EngineLayout";
import WithdrawalForm from "@/shared/components/withdrawals/WithdrawalForm";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import * as transactionService from "@/shared/api/services/transactionService";
import * as engineService from "@/shared/api/services/engineService";
import { mapGlobalTransactions } from "@/shared/utils/transactionMappers";

const statusConfig = {
  success: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: "completed" },
  pending: { icon: Clock, color: "bg-amber-100 text-amber-700", label: "pending" },
  processing: { icon: Loader2, color: "bg-blue-100 text-blue-700", label: "processingLabel" },
  failed: { icon: XCircle, color: "bg-red-100 text-red-700", label: "failed" },
  declined: { icon: XCircle, color: "bg-red-100 text-red-700", label: "declined" },
  cancelled: { icon: XCircle, color: "bg-slate-100 text-slate-700", label: "cancelled" },
  expired: { icon: XCircle, color: "bg-slate-100 text-slate-700", label: "expired" },
};

export default function EngineWithdrawals() {
  const { t } = useLanguage();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [merchantFilter, setMerchantFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const { data: profile } = useQuery({
    queryKey: ["engine-profile"],
    queryFn: () => engineService.getProfile(),
  });

  const resellerName = profile?.name || "Engine User";

  const [merchantIds, setMerchantIds] = useState([]);

  // Fetch All Merchants (Engine's + Resellers')
  const { data: allMerchants = [], isLoading: merchantsLoading } = useQuery({
    queryKey: ["engine-all-merchants"],
    queryFn: async () => {
      let allMerchantIdes = [];
      let allMerchantsList = [];
      
      // 1. Get all merchants created by engine logged in user
      const ownedMerchants = await engineService.getMyMerchants();
      ownedMerchants.forEach(m => {
        allMerchantIdes.push(m.id);
        allMerchantsList.push(m);
      });
      
      // 2. Get all the resellers created by engine logged in user
      const resellers = await engineService.getMyResellers();
      const resellerIdes = resellers.map(r => r.id);
      
      // 3. Then one by one get all the merchant ides created by resellers using resellerIdes
      for (const rId of resellerIdes) {
        const resellerMerchants = await engineService.getResellerMerchants(rId);
        resellerMerchants.forEach(m => {
          if (!allMerchantIdes.includes(m.id)) {
            allMerchantIdes.push(m.id);
            allMerchantsList.push(m);
          }
        });
      }
      
      return allMerchantsList;
    },
    refetchInterval: 60000,
  });

  useEffect(() => {
    if (allMerchants.length > 0) {
      setMerchantIds(allMerchants.map(m => m.id));
    } else if (!merchantsLoading) {
      setMerchantIds(["NO_MERCHANT_FOUND"]);
    }
  }, [allMerchants, merchantsLoading]);

  const { data: paginationResult, isLoading: transactionsLoading } = useQuery({
    queryKey: ["engine-withdrawals-paginated", search, statusFilter, merchantFilter, dateRange, merchantIds],
    queryFn: () => {
      const filters = { type: "withdrawal" };
      if (statusFilter !== "all") filters.status = statusFilter;
      if (dateRange.from) filters.startDate = dateRange.from.toISOString();
      if (dateRange.to) filters.endDate = dateRange.to.toISOString();

      return transactionService.getPaginatedTransactions(
        1,
        100,
        search,
        filters,
        merchantFilter !== "all" ? [merchantFilter] : merchantIds
      );
    },
    enabled: merchantIds.length > 0,
    keepPreviousData: true,
  });

  const transactions = useMemo(() => {
    return mapGlobalTransactions(paginationResult?.data || []);
  }, [paginationResult]);

  const isLoading = transactionsLoading || (merchantsLoading && !merchantIds.length);

  const merchants = useMemo(() => {
    return (allMerchants || []).map(m => ({
      id: m.id,
      name: m.name || m.code || m.id
    }));
  }, [allMerchants]);

  const merchantCurrencyMap = useMemo(() => {
    const map = new Map();
    (allMerchants || []).forEach((m) => {
      if (m?.id) map.set(m.id, m.currency || "THB");
    });
    return map;
  }, [allMerchants]);

  const copyToClipboard = (e, id) => {
    if (e) e.stopPropagation();
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setMerchantFilter("all");
    setDateRange({ from: null, to: null });
  };

  const hasFilters = search || statusFilter !== "all" || merchantFilter !== "all" || dateRange.from;

  const stats = useMemo(() => {
    const success = transactions.filter(tx => ["success", "paid", "completed"].includes(String(tx.status || "").toLowerCase()));
    const pending = transactions.filter(tx => ["pending", "processing"].includes(String(tx.status || "").toLowerCase()));
    return {
      total: success.reduce((sum, tx) => sum + Number(tx.amount || 0), 0),
      pendingCount: pending.length,
      pendingAmount: pending.reduce((sum, tx) => sum + Number(tx.amount || 0), 0),
    };
  }, [transactions]);

  const handleWithdrawalSubmit = async (data) => {
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 1500));
    setSubmitting(false);
    toast.success(`${t("withdrawalRequestSubmittedSuccessfully")} ${t("type")}: ${data.type}`);
  };

  const TransactionRowSkeleton = () => (
    <tr className="animate-pulse">
      <td className="px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-slate-100" />
          <div className="space-y-2">
            <div className="h-4 w-24 bg-slate-100 rounded" />
            <div className="h-3 w-16 bg-slate-50 rounded" />
          </div>
        </div>
      </td>
      <td className="px-4 py-4"><div className="h-4 w-24 bg-slate-50 rounded" /></td>
      <td className="px-4 py-4"><div className="h-4 w-16 bg-slate-50 rounded" /></td>
      <td className="px-4 py-4 text-right"><div className="h-4 w-20 bg-slate-50 rounded ml-auto" /></td>
      <td className="px-4 py-4"><div className="h-7 w-24 bg-slate-50 rounded-full" /></td>
      <td className="px-4 py-4"><div className="h-4 w-24 bg-slate-50 rounded" /></td>
    </tr>
  );

  return (
    <EngineLayout currentPage="EngineWithdrawals" resellerName={resellerName}>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("withdrawals")}</h1>
            <p className="text-slate-500">{t("requestPayoutsViaBankUsdt")}</p>
          </div>
        </div>

        <Tabs defaultValue="history" className="space-y-6">
          <TabsList className="bg-white border p-1">
            <TabsTrigger value="new" className="gap-2 px-4">
              <Plus className="h-4 w-4" />
              {t("newWithdrawal")}
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2 px-4">
              <History className="h-4 w-4" />
              {t("history")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="new">
            <WithdrawalForm
              onSubmit={handleWithdrawalSubmit}
              submitting={submitting}
              stats={stats}
              accentColor="sky"
            />
          </TabsContent>

          <TabsContent value="history">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <p className="text-sm text-slate-500">{t("totalWithdrawn")}</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">RS{stats.total.toLocaleString()}</p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <p className="text-sm text-slate-500">{t("completed")}</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">
                  {transactions.filter(tx => ["success", "paid", "completed"].includes(String(tx.status || "").toLowerCase())).length}
                </p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <p className="text-sm text-slate-500">{t("pending")}</p>
                <p className="text-2xl font-bold text-amber-600 mt-1">{stats.pendingCount}</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 mb-4">
              <div className="flex flex-wrap gap-3">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder={t("searchByTransactionId")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>

                <Select value={merchantFilter} onValueChange={setMerchantFilter}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder={t("merchant")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("allMerchants")}</SelectItem>
                    {merchants.map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder={t("status")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("allStatus")}</SelectItem>
                    <SelectItem value="success">{t("completed")}</SelectItem>
                    <SelectItem value="pending">{t("pending")}</SelectItem>
                    <SelectItem value="processing">{t("processingLabel")}</SelectItem>
                    <SelectItem value="failed">{t("failed")}</SelectItem>
                  </SelectContent>
                </Select>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      {dateRange.from ? (
                        dateRange.to ? (
                          `${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "MMM d")}`
                        ) : format(dateRange.from, "MMM d, yyyy")
                      ) : t("dateRange")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="range"
                      selected={dateRange}
                      onSelect={(range) => setDateRange(range || { from: null, to: null })}
                      numberOfMonths={2}
                    />
                  </PopoverContent>
                </Popover>

                {hasFilters && (
                  <Button variant="ghost" onClick={clearFilters} className="text-slate-500">
                    <X className="h-4 w-4 mr-1" />
                    {t("clear")}
                  </Button>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("transaction")}</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("merchant")}</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("method")}</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("amount")}</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("status")}</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("date")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {isLoading && !transactions.length ? (
                      Array.from({ length: 10 }).map((_, i) => (
                        <TransactionRowSkeleton key={i} />
                      ))
                    ) : transactions.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-12 text-slate-500">{t("noWithdrawalsFound")}</td></tr>
                    ) : (
                      transactions.map((payout, index) => {
                        const statusKey = String(payout.status || "").toLowerCase();
                        const status = statusConfig[statusKey] || statusConfig.pending;
                        const StatusIcon = status.icon;

                        return (
                          <motion.tr
                            key={payout.id || index}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.02 * index }}
                            onClick={() => setSelectedPayout(payout)}
                            className="hover:bg-slate-50 transition-colors cursor-pointer"
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-lg bg-sky-100 flex items-center justify-center">
                                  <ArrowUpFromLine className="h-4 w-4 text-sky-600" />
                                </div>
                                <div>
                                  <div className="flex items-center gap-1.5">
                                    <span className="font-mono text-sm font-medium">
                                      {(payout.id || "").slice(0, 12)}...
                                    </span>
                                    <button
                                      onClick={(e) => copyToClipboard(e, payout.id)}
                                      className="text-slate-400 hover:text-slate-600"
                                    >
                                      {copiedId === payout.id ? (
                                        <Check className="h-3.5 w-3.5 text-emerald-500" />
                                      ) : (
                                        <Copy className="h-3.5 w-3.5" />
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm text-slate-900">{payout.merchant?.name || payout.merchant?.code || t("nA")}</span>
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant="outline" className="text-xs uppercase">
                                {payout.paymentMethod || t("nA")}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="font-semibold text-sky-600">
                                {(() => {
                                  const currency = payout.currency || "PKR";
                                  const symbol = currency === "PKR" ? "Rs" : "฿";
                                  return `${symbol}${Number(payout.amount || 0).toLocaleString()}`;
                                })()}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <Badge className={`${status.color} gap-1 border-0`}>
                                <StatusIcon className={`h-3 w-3 ${statusKey === "processing" ? "animate-spin" : ""}`} />
                                {t(status.label)}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-500">
                              {payout.createdAt ? format(new Date(payout.createdAt), "MMM d, HH:mm") : t("nA")}
                            </td>
                          </motion.tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Payout Detail Sheet */}
        <Sheet open={!!selectedPayout} onOpenChange={() => setSelectedPayout(null)}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
            {selectedPayout && (
              <>
                <SheetHeader className="pb-4 border-b">
                  <SheetTitle className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
                      <ArrowUpFromLine className="h-5 w-5 text-sky-600" />
                    </div>
                    <div>
                      <p className="text-lg">{t("withdrawal")}</p>
                      <Badge className={`${statusConfig[String(selectedPayout.status || "").toLowerCase()]?.color || statusConfig.pending.color} border-0`}>
                        {t(statusConfig[String(selectedPayout.status || "").toLowerCase()]?.label || statusConfig.pending.label)}
                      </Badge>
                    </div>
                  </SheetTitle>
                </SheetHeader>

                <div className="py-6 space-y-6">
                  <div className="text-center py-4 bg-slate-50 rounded-xl">
                    <p className="text-sm text-slate-500 mb-1">{t("amount")}</p>
                    <p className="text-3xl font-bold text-sky-600">
                      {(() => {
                        const currency = selectedPayout.currency || "PKR";
                        const symbol = currency === "PKR" ? "Rs" : "฿";
                        return `${symbol}${Number(selectedPayout.amount || 0).toLocaleString()}`;
                      })()}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-500">{t("merchant")}</p>
                        <p className="font-medium">{selectedPayout.merchant?.name || selectedPayout.merchant?.code || t("nA")}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">{t("provider")}</p>
                        <p className="font-medium">{selectedPayout.provider?.code || t("nA")}</p>
                      </div>
                    </div>

                    <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Building className="h-4 w-4" />
                        <span className="text-sm font-medium">{t("bankDetails")}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-slate-500">{t("bank")}</p>
                          <p className="font-medium">{selectedPayout.bank_code || selectedPayout.bankCode || t("nA")}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">{t("account")}</p>
                          <p className="font-medium font-mono">{selectedPayout.bank_account || selectedPayout.bankAccount || t("nA")}</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-slate-500 mb-2">{t("transactionId")}</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-slate-100 px-3 py-2 rounded-lg font-mono text-sm">
                          {selectedPayout.id}
                        </code>
                        <Button variant="outline" size="icon" onClick={() => copyToClipboard(null, selectedPayout.id)}>
                          {copiedId === selectedPayout.id ? (
                            <Check className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-slate-500 mb-2">{t("date")}</p>
                      <p className="font-medium">
                        {selectedPayout.createdAt ? format(new Date(selectedPayout.createdAt), "PPP p") : t("nA")}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </EngineLayout>
  );
}